import type { BackofficePermissionCode } from '@kardume/types';

export type BackofficeSectionId =
  | 'dashboard'
  | 'merchants'
  | 'features'
  | 'customers'
  | 'leads'
  | 'messaging'
  | 'categories'
  | 'amenities'
  | 'payment-methods'
  | 'localities'
  | 'feeds'
  | 'trails'
  | 'collections'
  | 'moderation'
  | 'experience-moderation'
  | 'claims'
  | 'profile-edits'
  | 'promotion-rules'
  | 'benefit-types'
  | 'plans'
  | 'subscriptions'
  | 'billing'
  | 'influencers'
  | 'reports'
  | 'merchant-interest'
  | 'search-terms'
  | 'opportunities'
  | 'audit'
  | 'employees'
  | 'access-profiles';

/**
 * Os grupos do menu lateral, na ordem em que aparecem.
 *
 * O critério é a pergunta que a pessoa traz ao abrir o backoffice, não o
 * assunto da tela:
 *
 * - `Operação`  - "o que precisa da minha decisão hoje?" Filas e cadastro que
 *   se olha no turno: reivindicações, anúncios para moderar, a carteira.
 * - `Comercial` - "de onde vem a próxima parceira?" O funil inteiro na ordem em
 *   que ele acontece: medição da procura, alvo, negociação, contato.
 * - `Plataforma`- catálogo e conteúdo, que se mexe de vez em quando e vale para
 *   todo mundo: categorias, cidades, matérias, coleções, regras, planos.
 * - `Financeiro`- dinheiro que entra e o que se presta de conta dele.
 * - `Acesso`    - quem entra aqui e o que cada um pode fazer.
 *
 * O grupo `Marketing` deixou de existir: tinha um item só (Disparos), e um
 * título sobre uma linha só custa uma linha e não ensina nada. Disparos foi
 * para `Comercial`, que é onde a lista de prospecção dele é montada.
 */
export type BackofficeNavGroup = 'Operação' | 'Comercial' | 'Plataforma' | 'Financeiro' | 'Acesso';

export interface BackofficeNavItem {
  section: BackofficeSectionId;
  group: BackofficeNavGroup;
  label: string;
  /** Any of these permissions grants access (admins always pass). */
  permissions: BackofficePermissionCode[];
}

export const BACKOFFICE_NAV_ITEMS: BackofficeNavItem[] = [
  { section: 'dashboard', group: 'Operação', label: 'Dashboard', permissions: ['ROLE_BACKOFFICE_DASHBOARD_READ'] },
  { section: 'merchants', group: 'Operação', label: 'Lojistas', permissions: ['ROLE_BACKOFFICE_MERCHANT_READ', 'ROLE_BACKOFFICE_MERCHANT_MANAGE', 'ROLE_BACKOFFICE_PARTNERSHIP_MANAGE'] },
  // Card 262 - logo abaixo de Lojistas, porque é a mesma carteira vista pelo
  // outro eixo: lá se pergunta "esta empresa está em destaque?", aqui "o que
  // está em destaque em Toledo?". Fica em Operação, e não em Comercial, porque
  // é a execução de uma exposição já vendida - quem opera destaque atende
  // contrato, não prospecta. Sem permissão nova: quem lê o cadastro ou
  // gerencia parceria já lê destaque no drawer, e uma FEATURE_READ só valeria
  // se existisse alguém que vende exposição sem poder mexer na parceria.
  { section: 'features', group: 'Operação', label: 'Destaques', permissions: ['ROLE_BACKOFFICE_MERCHANT_READ', 'ROLE_BACKOFFICE_PARTNERSHIP_MANAGE'] },
  // As duas filas de decisão ficam juntas: são "alguém está esperando uma
  // resposta nossa", e quem abre uma abre a outra no mesmo turno.
  { section: 'claims', group: 'Operação', label: 'Reivindicações', permissions: ['ROLE_BACKOFFICE_CLAIM_READ', 'ROLE_BACKOFFICE_CLAIM_MANAGE'] },
  // Card 277 - logo abaixo de Reivindicações porque é a continuação dela: quem
  // aprovou o pedido é quem depois decide as mudanças que o responsável propõe,
  // e o anexo que sustenta as duas decisões é o mesmo. `CLAIM_READ` entra na
  // lista porque o core deixa a fila ser lida por quem opera reivindicações -
  // ler a fila é triagem; decidir exige PROFILE_EDIT_MODERATE, e a tela esconde
  // os botões de quem não tem.
  { section: 'profile-edits', group: 'Operação', label: 'Moderação de edições', permissions: ['ROLE_BACKOFFICE_CLAIM_READ', 'ROLE_BACKOFFICE_PROFILE_EDIT_MODERATE'] },
  { section: 'moderation', group: 'Operação', label: 'Moderação de anúncios', permissions: ['ROLE_BACKOFFICE_PROMOTION_MODERATE'] },
  // Card #352 - colada na moderação de anúncios porque é a mesma pergunta do
  // turno ("o que precisa da minha decisão hoje?"), mas com permissão própria:
  // no anúncio se confere preço e regra comercial, na experiência se confere se
  // a descrição corresponde ao que a pessoa vai viver. Como toda experiência
  // nasce PENDING_REVIEW, sem esta seção a vitrine de experiências nunca ganha
  // conteúdo - perfis de acesso criados antes do card precisam marcar a
  // permissão para enxergá-la.
  { section: 'experience-moderation', group: 'Operação', label: 'Moderação de experiências', permissions: ['ROLE_BACKOFFICE_EXPERIENCE_MODERATE'] },
  { section: 'customers', group: 'Operação', label: 'Usuários', permissions: ['ROLE_BACKOFFICE_CUSTOMER_READ', 'ROLE_BACKOFFICE_CUSTOMER_MANAGE'] },
  // Comercial na ordem do funil, de cima para baixo - é assim que o dia anda:
  //   Interesse por empresa  onde há procura (card 258)
  //   Oportunidades          para quem ligar (card 238)
  //   Leads                  quem já levantou a mão
  //   Disparos               o contato em si
  // Antes os três primeiros estavam em Operação, o que empurrava o grupo para
  // nove itens e misturava fila de decisão com prospecção. São trabalhos de
  // gente diferente em momentos diferentes.
  { section: 'merchant-interest', group: 'Comercial', label: 'Interesse por empresa', permissions: ['ROLE_BACKOFFICE_REPORT_READ'] },
  // Card 346 - logo abaixo de "Interesse por empresa", porque é a mesma etapa do
  // funil vista pelo outro lado: lá se pergunta "onde há procura por quem já
  // está no guia?", aqui "o que procuram e o guia não tem?". A permissão é
  // própria e NÃO a de relatórios: aqui se lê texto digitado por pessoas, e o
  // critério da separação é privacidade, não curadoria.
  { section: 'search-terms', group: 'Comercial', label: 'Termos buscados', permissions: ['ROLE_BACKOFFICE_SEARCH_TERM_READ'] },
  // Card 238 - a permissão é a de LEADS, e não a de relatórios: a tela escreve
  // (gera lead, marca resultado). Quem só lê relatório não pode marcar "virou
  // parceira".
  { section: 'opportunities', group: 'Comercial', label: 'Oportunidades', permissions: ['ROLE_BACKOFFICE_LEAD_READ', 'ROLE_BACKOFFICE_LEAD_MANAGE'] },
  { section: 'leads', group: 'Comercial', label: 'Leads', permissions: ['ROLE_BACKOFFICE_LEAD_READ', 'ROLE_BACKOFFICE_LEAD_MANAGE'] },
  { section: 'messaging', group: 'Comercial', label: 'Disparos', permissions: ['ROLE_BACKOFFICE_MESSAGING_READ', 'ROLE_BACKOFFICE_MESSAGING_MANAGE', 'ROLE_BACKOFFICE_MESSAGING_SEND'] },
  { section: 'categories', group: 'Plataforma', label: 'Categorias', permissions: ['ROLE_BACKOFFICE_CATEGORY_MANAGE'] },
  // Card 305 - ao lado de Categorias porque é o mesmo tipo de coisa: vocabulário
  // da plataforma que se mexe de vez em quando e vale para todo mundo. A
  // permissão é própria e não a de categorias: quem organiza as categorias do
  // guia não é necessariamente quem decide se "sala para reunião" vira
  // comodidade - e criar uma acrescenta uma caixa no perfil de todo lojista.
  { section: 'amenities', group: 'Plataforma', label: 'Comodidades', permissions: ['ROLE_BACKOFFICE_AMENITY_READ', 'ROLE_BACKOFFICE_AMENITY_MANAGE'] },
  // Card 311 - ao lado de Comodidades porque é o mesmo tipo de coisa e virou
  // catálogo pelo mesmo motivo: era enum no código, e "vale-cultura" custava um
  // deploy. Permissão própria, e não a de comodidades: quem decide se "sala
  // para reunião" vira comodidade não é necessariamente quem decide se o
  // Kardume passa a listar cripto.
  { section: 'payment-methods', group: 'Plataforma', label: 'Formas de pagamento', permissions: ['ROLE_BACKOFFICE_PAYMENT_METHOD_READ', 'ROLE_BACKOFFICE_PAYMENT_METHOD_MANAGE'] },
  { section: 'localities', group: 'Plataforma', label: 'Localidades', permissions: ['ROLE_BACKOFFICE_LOCALITY_READ', 'ROLE_BACKOFFICE_LOCALITY_MANAGE'] },
  // Conteúdo editorial das páginas de cidade: fica em Plataforma, ao lado de
  // Localidades, porque uma matéria só existe amarrada a uma cidade (ou a
  // todas) e a uma categoria - é catálogo, não fila de trabalho diária.
  { section: 'feeds', group: 'Plataforma', label: 'Feeds e matérias', permissions: ['ROLE_BACKOFFICE_FEED_READ', 'ROLE_BACKOFFICE_FEED_MANAGE'] },
  // Card 222 - colada em Feeds, e não perto de Categorias, porque é a mesma
  // natureza de trabalho: alguém decide o recorte e escreve o texto do cartão.
  // A permissão é literalmente a de Feeds, e não uma nova: o `@PreAuthorize` do
  // `BackofficeExploreTrailController` exige exatamente
  // ROLE_ADMIN | ROLE_BACKOFFICE_FEED_READ | ROLE_BACKOFFICE_FEED_MANAGE para
  // ler e ROLE_ADMIN | ROLE_BACKOFFICE_FEED_MANAGE para escrever e para a
  // prévia. Um par ROLE_BACKOFFICE_TRAIL_* obrigaria a conceder dois papéis
  // para a mesma pessoa fazer o mesmo trabalho, e o primeiro esquecido viraria
  // uma tela cinza sem explicação.
  { section: 'trails', group: 'Plataforma', label: 'Trilhas do /explorar', permissions: ['ROLE_BACKOFFICE_FEED_READ', 'ROLE_BACKOFFICE_FEED_MANAGE'] },
  // Coleções ficam ao lado de Feeds pelo mesmo motivo: é conteúdo editorial
  // amarrado a uma cidade (ou a todas), não fila de trabalho diária. A
  // permissão é própria e não a de Feeds - quem escreve uma matéria não é
  // necessariamente quem decide quais dez lojas o Kardume recomenda.
  { section: 'collections', group: 'Plataforma', label: 'Coleções', permissions: ['ROLE_BACKOFFICE_COLLECTION_READ', 'ROLE_BACKOFFICE_COLLECTION_MANAGE'] },
  // Fica em Plataforma junto de Categorias e Localidades: é catálogo da
  // plataforma, não uma fila de trabalho diária. A permissão continua sendo a
  // da moderação, e não uma nova: o controller do core exige exatamente
  // ROLE_BACKOFFICE_PROMOTION_MODERATE - quem aprova o texto de um anúncio é
  // quem escreve as cláusulas que ele oferece.
  { section: 'promotion-rules', group: 'Plataforma', label: 'Regras de anúncio', permissions: ['ROLE_BACKOFFICE_PROMOTION_MODERATE'] },
  // Card #361 - ao lado de Regras de anúncio porque é o mesmo tipo de coisa: o
  // catálogo que o formulário do lojista oferece, mantido pela operação. Mesma
  // permissão, e não uma nova, pelo mesmo critério que as cláusulas seguem -
  // quem aprova o texto de um anúncio é quem define as ofertas que ele pode
  // fazer. Uma permissão só para tipos de benefício criaria um perfil que
  // escreve o catálogo sem poder olhar o anúncio que o usa.
  { section: 'benefit-types', group: 'Plataforma', label: 'Tipos de benefício', permissions: ['ROLE_BACKOFFICE_PROMOTION_MODERATE'] },
  { section: 'plans', group: 'Plataforma', label: 'Kardume Pass', permissions: ['ROLE_BACKOFFICE_PASS_PLAN_MANAGE'] },
  { section: 'subscriptions', group: 'Financeiro', label: 'Assinaturas', permissions: ['ROLE_BACKOFFICE_SUBSCRIPTION_MANAGE'] },
  { section: 'billing', group: 'Financeiro', label: 'Financeiro', permissions: ['ROLE_BACKOFFICE_BILLING_MANAGE'] },
  // Spec 038 - fica em Financeiro, que é onde se pergunta "o que entra e o que
  // se presta de conta dele": aqui se presta conta de dinheiro que SAI, para
  // uma pessoa física. Permissões próprias, e não as de `BILLING_MANAGE`: quem
  // concilia fatura de lojista não é necessariamente quem negocia contrato com
  // influenciador, e este é o único lugar do backoffice onde se lê quanto cada
  // pessoa recebe.
  { section: 'influencers', group: 'Financeiro', label: 'Influenciadores', permissions: ['ROLE_BACKOFFICE_INFLUENCER_READ', 'ROLE_BACKOFFICE_INFLUENCER_MANAGE'] },
  { section: 'reports', group: 'Financeiro', label: 'Relatórios', permissions: ['ROLE_BACKOFFICE_REPORT_READ'] },
  { section: 'audit', group: 'Acesso', label: 'Logs', permissions: ['ROLE_BACKOFFICE_AUDIT_READ'] },
  { section: 'employees', group: 'Acesso', label: 'Funcionários', permissions: ['ROLE_BACKOFFICE_EMPLOYEE_READ', 'ROLE_BACKOFFICE_EMPLOYEE_MANAGE'] },
  { section: 'access-profiles', group: 'Acesso', label: 'Perfis de acesso', permissions: ['ROLE_BACKOFFICE_ACCESS_PROFILE_MANAGE'] },
];

export const BACKOFFICE_NAV_GROUP_ORDER: BackofficeNavGroup[] = [
  'Operação',
  'Comercial',
  'Plataforma',
  'Financeiro',
  'Acesso',
];

export const hasAnyPermission = (
  available: readonly string[] | undefined,
  required: readonly string[],
  isAdmin = false
): boolean => {
  if (isAdmin) return true;
  if (required.length === 0) return true;
  if (!available || available.length === 0) return false;
  return required.some((code) => available.includes(code));
};

export const getVisibleNavItems = (available: readonly string[] | undefined, isAdmin = false) =>
  BACKOFFICE_NAV_ITEMS.filter((item) => hasAnyPermission(available, item.permissions, isAdmin));

export const getInitialSection = (
  available: readonly string[] | undefined,
  preferred: BackofficeSectionId | null,
  isAdmin = false
): BackofficeSectionId | null => {
  const visible = getVisibleNavItems(available, isAdmin);
  if (preferred && visible.some((item) => item.section === preferred)) return preferred;
  return visible[0]?.section ?? null;
};

export const isSectionVisible = (
  available: readonly string[] | undefined,
  section: BackofficeSectionId,
  isAdmin = false
) => getVisibleNavItems(available, isAdmin).some((item) => item.section === section);
